import { BKJTextInput, Flex } from '@bookjane2/bookjane-design-library';
import { useTranslation } from 'hooks/useTranslation';
import { FC, useCallback, useState } from 'react';
import { AddNewCustomerFormWrapper, SectionTitle } from './CustomersSettingsPage.styled';
import { CustomersSettingsPageHeader, LocationSection, FormActions } from './components';
import { RemoveCustomerLocationDialog } from './RemoveCustomerLocationDialog/RemoveCustomerLocationDialog';
import {
  CustomersSettingsPageFormProvider,
  useCustomersSettingsPageFormContext,
} from 'pages/Settings/pages/LocationSettings/CustomersSettingsPage/CustomersSettingsPage.form.context';
import { SubFacilityAddress } from 'types/graphql';
import { CustomerLocationGroupedForm } from 'pages/Settings/pages/LocationSettings/CustomersSettingsPage/CustomersSettingsPage.form.types';
import { useHistory, useParams } from 'react-router-dom';
import {
  NEW_LOCATION_ID_PREFIX,
  isNewLocationId,
  getNewLocationIndex,
} from 'pages/Settings/pages/LocationSettings/CustomersSettingsPage/CustomersSettingsPage.constants';

export const EditCustomerForm: FC = () => {
  const { t } = useTranslation();
  const { values, groupedFormsBehaviors, isEditMode } = useCustomersSettingsPageFormContext();
  const history = useHistory();
  const { locationId } = useParams<{ locationId: string }>();

  const { groupedForms, onChange, removeFormItem, onSubmit } = groupedFormsBehaviors;

  const locations = groupedForms as CustomerLocationGroupedForm[];

  const [isRemoveLocationModalOpen, setIsRemoveLocationModalOpen] = useState(false);
  const [locationIdToRemove, setLocationIdToRemove] = useState<string | null>(null);

  const removeNewLocation = useCallback(
    (index: number) => {
      const location = locations[index];
      if (location) {
        location.values.id = index.toString();
        removeFormItem({ id: index.toString() });
      }
    },
    [locations, removeFormItem],
  );

  const onRemoveLocationSuccess = useCallback(() => {
    if (locationIdToRemove) {
      if (isNewLocationId(locationIdToRemove)) {
        const index = getNewLocationIndex(locationIdToRemove);
        removeNewLocation(index);
      } else {
        history.push(`/settings/locations/${locationId}/customers`);
      }
    }
    setLocationIdToRemove(null);
    setIsRemoveLocationModalOpen(false);
  }, [locationIdToRemove, removeNewLocation, history, locationId]);

  return (
    <AddNewCustomerFormWrapper onSubmit={(e) => onSubmit(e)}>
      <CustomersSettingsPageHeader
        title={
          isEditMode
            ? t('location_settings:EDIT_CUSTOMER')
            : t('location_settings:ADD_NEW_CUSTOMER')
        }
      />

      {/* Customer Information Section */}
      <Flex gap="24px" flexDirection="column">
        <SectionTitle>{t('location_settings:CUSTOMER_INFORMATION')}</SectionTitle>
        <BKJTextInput
          label={`${t('location_settings:CUSTOMER_NAME')}*`}
          name="name"
          value={values.name}
          onChange={onChange}
        />
      </Flex>

      {locations.map((location, index) => (
        <LocationSection
          key={location.values.displayId || `${NEW_LOCATION_ID_PREFIX}${index}`}
          location={location.values as SubFacilityAddress}
          index={index}
          onRemoveLocation={() => {
            if (!location.values.displayId) {
              setLocationIdToRemove(`${NEW_LOCATION_ID_PREFIX}${index}`);
              setIsRemoveLocationModalOpen(true);
            } else {
              setLocationIdToRemove(location.values.displayId.toString());
              setIsRemoveLocationModalOpen(true);
            }
          }}
          onLocationChange={(e) => location.onChange(e)}
        />
      ))}

      <FormActions />

      {locationIdToRemove && (
        <RemoveCustomerLocationDialog
          addressId={locationIdToRemove}
          locationName={
            isNewLocationId(locationIdToRemove)
              ? locations[getNewLocationIndex(locationIdToRemove)]?.values?.name
              : locations.find((loc) => loc.values.displayId?.toString() === locationIdToRemove)
                  ?.values?.name
          }
          isRemoveCustomerLocationConfirmationOpen={isRemoveLocationModalOpen}
          setIsRemoveCustomerLocationConfirmationOpen={setIsRemoveLocationModalOpen}
          onRemoveComplete={onRemoveLocationSuccess}
        />
      )}
    </AddNewCustomerFormWrapper>
  );
};

export const EditView: FC = () => {
  return (
    <CustomersSettingsPageFormProvider>
      <EditCustomerForm />
    </CustomersSettingsPageFormProvider>
  );
};
